/**
 * Project Meetings Page
 * Unified interface for viewing and managing project and organization meetings
 */

import { useState } from 'react';
import { useParams } from 'react-router';
import { Plus } from 'lucide-react';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import MeetingManager from '../components/MeetingManager';
import { ProjectMeeting, MeetingActionItem, Task } from '../types';
import { generateId } from '../utils/idGenerator';
import { addTask } from '../store';
import { toast } from 'sonner';

export default function ProjectMeetingsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { state } = useApp();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [localMeetings, setLocalMeetings] = useState<ProjectMeeting[]>([]);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formTime, setFormTime] = useState('09:00');
  const [formDuration, setFormDuration] = useState('60');
  const [formAttendeeIds, setFormAttendeeIds] = useState<string[]>([]);

  const project = state?.projects?.find(
    (p) => String(p.id).toLowerCase() === String(projectId).toLowerCase()
  );

  const { projects, users } = useOrganizationData(state, {
    includeAllOrganizations: true,
  }) as {
    projects: any[];
    users: any[];
  };

  if (!project) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Project not found
      </div>
    );
  }

  const currentUser = state?.currentUser;
  const orgMeetings: any[] = [];

  if (!currentUser) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        User not found
      </div>
    );
  }

  const handleCreateMeeting = () => {
    if (!formTitle.trim() || !formDate) return;
    const scheduledAt = new Date(`${formDate}T${formTime}`).toISOString();
    const newMeeting: ProjectMeeting = {
      id: generateId('mtg'),
      organizationId: project.organizationId,
      projectId: project.id,
      title: formTitle.trim(),
      description: formDescription.trim() || undefined,
      scheduledAt,
      duration: parseInt(formDuration, 10) || 60,
      attendeeIds: formAttendeeIds,
      notes: '',
      actionItems: [],
      createdAt: new Date().toISOString(),
      createdBy: currentUser.id,
    };
    setLocalMeetings((prev) => [...prev, newMeeting]);
    setShowCreateForm(false);
    setFormTitle('');
    setFormDescription('');
    setFormDate('');
    setFormTime('09:00');
    setFormDuration('60');
    setFormAttendeeIds([]);
  };

  const handleUpdateMeeting = (updated: ProjectMeeting) => {
    setLocalMeetings((prev) =>
      prev.map((m) => (m.id === updated.id ? { ...updated, updatedAt: new Date().toISOString() } : m))
    );
  };

  const handleToggleActionItem = (meetingId: string, itemId: string) => {
    setLocalMeetings((prev) =>
      prev.map((m) => {
        if (m.id !== meetingId) return m;
        return {
          ...m,
          actionItems: (m.actionItems || []).map((ai) =>
            ai.id === itemId ? { ...ai, done: !ai.done } : ai
          ),
        };
      })
    );
  };

  const handleAddActionItem = (meetingId: string, text: string, assigneeId?: string) => {
    const newItem: MeetingActionItem = {
      id: generateId('ai'),
      text,
      assigneeId,
      done: false,
      createdAt: new Date().toISOString(),
    };
    setLocalMeetings((prev) =>
      prev.map((m) =>
        m.id === meetingId
          ? { ...m, actionItems: [...(m.actionItems || []), newItem] }
          : m
      )
    );
  };

  const handleCreateTaskFromActionItem = async (meetingId: string, itemId: string) => {
    const meeting = localMeetings.find((m) => m.id === meetingId);
    if (!meeting) return;

    const actionItem = (meeting.actionItems || []).find((item) => item.id === itemId);
    if (!actionItem || actionItem.linkedTaskId) return;

    const orgId = meeting.organizationId || project.organizationId;
    const newTaskId = generateId('task', project.id, meetingId);

    const linkToMeeting = `/app/projects/${project.id}/meetings#${meeting.id}`;
    const newTask: Task = {
      id: newTaskId,
      organizationId: orgId,
      projectId: project.id,
      title: actionItem.text,
      description: `Created from meeting "${meeting.title}" action item.`,
      type: 'Feature',
      status: 'Not Started',
      priority: 'Medium',
      assigneeIds: actionItem.assigneeId ? [actionItem.assigneeId] : [],
      parentTaskIds: [],
      subtaskIds: [],
      estimatedDays: 0,
      timeLogs: [],
      standupNotes: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      customerVisible: false,
      attachments: [],
      verificationLinks: [],
      externalLinks: [{ type: 'Meeting', url: linkToMeeting }],
      costItemIds: [],
      changeLogs: [],
    };

    try {
      await addTask(newTask);

      setLocalMeetings((prev) =>
        prev.map((m) => {
          if (m.id !== meetingId) return m;

          const actionItems = (m.actionItems || []).map((item) =>
            item.id === itemId ? { ...item, linkedTaskId: newTaskId } : item
          );
          const relatedTaskIds = Array.from(new Set([...(m.relatedTaskIds || []), newTaskId]));

          return {
            ...m,
            actionItems,
            relatedTaskIds,
            updatedAt: new Date().toISOString(),
          };
        })
      );

      toast.success('Task created and linked to meeting action item');
    } catch (error) {
      console.error('[ProjectMeetingsPage] Failed to create task from action item:', error);
      toast.error('Failed to create task from action item');
    }
  };

  const projectUsers = users.filter((u: any) =>
    project.memberIds?.includes(u.id) ||
    project.teamMembers?.some((tm: any) => tm.userId === u.id)
  ).concat(users.filter((u: any) => u.id === currentUser.id));

  // Remove duplicate users
  const uniqueProjectUsers = Array.from(new Map(projectUsers.map((u: any) => [u.id, u])).values()) as typeof users;

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6 max-w-7xl mx-auto">
      <div className="mb-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="mb-1 text-2xl font-semibold text-foreground">Meetings</h1>
            <p className="text-sm text-muted-foreground">{project.name}</p>
          </div>
          <button
            onClick={() => setShowCreateForm((v) => !v)}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90 text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            New Meeting
          </button>
        </div>
      </div>

      {/* Create meeting form */}
      {showCreateForm && (
        <div className="mb-6 rounded-xl border border-border bg-card p-5 shadow-sm">
          <h2 className="mb-4 text-base font-semibold text-foreground">Schedule a Meeting</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Title *</label>
              <input
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="Meeting title"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Description</label>
              <textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Agenda or description (optional)"
                rows={2}
                className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Date *</label>
              <input
                type="date"
                title="Meeting date"
                value={formDate}
                onChange={(e) => setFormDate(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Time</label>
              <input
                type="time"
                title="Meeting time"
                value={formTime}
                onChange={(e) => setFormTime(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Duration (minutes)</label>
              <input
                type="number"
                min="15"
                step="15"
                title="Duration in minutes"
                value={formDuration}
                onChange={(e) => setFormDuration(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Attendees</label>
              <select
                multiple
                title="Select attendees"
                value={formAttendeeIds}
                onChange={(e) =>
                  setFormAttendeeIds(Array.from(e.target.selectedOptions, (o) => o.value))
                }
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                size={Math.min(4, users.length)}
              >
                {users.map((u: any) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">Hold Ctrl/Cmd to select multiple</p>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleCreateMeeting}
              disabled={!formTitle.trim() || !formDate}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              Create Meeting
            </button>
            <button
              onClick={() => setShowCreateForm(false)}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <MeetingManager
        projectMeetings={localMeetings}
        orgMeetings={orgMeetings}
        currentUser={currentUser}
        users={users}
        projects={projects}
        onCreateProjectMeeting={(m) => setLocalMeetings((prev) => [...prev, m])}
        onUpdateMeeting={handleUpdateMeeting as any}
        onToggleActionItem={handleToggleActionItem}
        onAddActionItem={handleAddActionItem}
        onCreateTaskFromActionItem={handleCreateTaskFromActionItem}
        projectUsers={uniqueProjectUsers}
      />
    </div>
  );
}
